import {
  compact,
  concat,
  find,
  get,
  getOr,
  set,
  map,
  filter,
  identity,
  toPairs,
  fromPairs,
  isEqual,
  isObject,
  flow,
  pick,
  join,
  size,
  isEmpty,
} from 'lodash/fp';
import getMapper, { cleanEmptyValues, mapPath } from 'helpers/mapper';
import { encodeName, decodeName } from 'helpers/fieldOptions';
import {
  options as carBodyTypeOptions,
  commercialOptions as commercialCarBodyTypeOptions,
} from 'helpers/fieldOptions/car/bodyType';
import { options as airbagOptions } from 'helpers/fieldOptions/car/airbag';
import { options as additionalOptions } from 'helpers/fieldOptions/car/additional';
import { options as comfortOptions } from 'helpers/fieldOptions/car/comfort';
import { options as colorOptions } from 'helpers/fieldOptions/car/color';
import { options as emissionStickerOptions } from 'helpers/fieldOptions/car/emissionSticker';
import { options as envStandardOptions } from 'helpers/fieldOptions/car/environmentalStandard';
import { options as consumptionOptions } from 'helpers/fieldOptions/car/consumption';
import { options as seatComfortOptions } from 'helpers/fieldOptions/car/seatComfort';
import { options as multimediaOptions } from 'helpers/fieldOptions/car/multimedia';
import { options as lightOptions } from 'helpers/fieldOptions/car/light';
import { options as distanceOptions } from 'helpers/fieldOptions/ad/distance';
import { options as metallicOptions } from 'helpers/fieldOptions/car/metallic';
import { options as fuelTypeOptions } from 'helpers/fieldOptions/car/fuelType';
import { options as transmissionOptions } from 'helpers/fieldOptions/car/transmission';
import { options as securityOptions } from 'helpers/fieldOptions/car/security';
import { options as interiorOptions } from 'helpers/fieldOptions/car/interior';
import { options as extraOptions } from 'helpers/fieldOptions/car/extra';
import { options as parkingSensorOptions } from 'helpers/fieldOptions/car/parkingSensor';
import { options as prevOwnerOptions } from 'helpers/fieldOptions/car/prevOwner';
import { options as sellerTypeOptions } from 'helpers/fieldOptions/ad/sellerType';
import { options as seatOptions } from 'helpers/fieldOptions/car/seat';
import { options as serviceHistoryOptions } from 'helpers/fieldOptions/car/serviceHistory';
import { options as upholsteryOptions } from 'helpers/fieldOptions/car/upholstery';
import { formatDistance, formatMoney, formatPower } from 'helpers/i18n';
import {
  DEFAULT_GEOLOCATION_DISTANCE,
  DISTANCE_NAME,
  GEOLOCATION_NAME,
  NOT_DAMAGED_NAME,
  SMOKER_NAME,
} from '../../constants';
import checkboxListToArray from '../checkboxListToArray';

const HP_TO_KW = 0.7457;

const checkboxListToArrayWithDecodeName = checkboxListToArray(decodeName);

const mapPathAndFormat =
  (pathTo, pathFrom = pathTo, format = identity) =>
  (acc, source) => {
    const value = get(pathFrom, source);

    if (value) {
      return set(
        pathTo,
        {
          value: format(value),
          form: set(pathFrom, value, {}),
        },
        acc,
      );
    }

    return acc;
  };

const movePath = (path, getter = get, ...args) => getter(path, ...args);

const getLabelsByValue = flow(
  toPairs,
  map(([, { label, value }]) => [value, label]),
  fromPairs,
);

const powerFormatter = (hp) =>
  formatPower({ hp, kw: Math.ceil(hp * HP_TO_KW) });

const mapCheckboxList = (path, options) => (acc, source) => {
  const value = get(path, source);

  if (value) {
    const checkboxListAsArray = isObject(value)
      ? checkboxListToArrayWithDecodeName(value)
      : [value];
    const labelsByValue = getLabelsByValue(options);

    return set(
      path,
      map(
        (item) => ({
          value: labelsByValue[item],
          form: set(
            path,
            isObject(value) ? pick([encodeName(item)], value) : item,
            {},
          ),
        }),
        checkboxListAsArray,
      ),
      acc,
    );
  }

  return acc;
};

const getMetallic = (path) => (acc, source) => {
  const metallicValue = get(['metallic', encodeName('true')], source);

  if (metallicValue) {
    const labelsByValue = getLabelsByValue(metallicOptions);

    return set(
      path,
      {
        value: labelsByValue[metallicValue],
        form: set(path, get(['metallic'], source), {}),
      },
      acc,
    );
  }

  return acc;
};

const getVehicle = (makeOptions) => (path) => (acc, source) => {
  const value = get(['vehicle'], source);
  const hasVehicles = flow(filter(get(['make'])), size, Boolean)(value);

  if (hasVehicles) {
    const makeLabels = getLabelsByValue(makeOptions);

    return set(
      path,
      map(
        (vehicle) => ({
          value: flow(
            compact,
            join(' '),
          )([
            get(get(['make'], vehicle), makeLabels),
            get(['model'], vehicle),
            get(['subType'], vehicle),
          ]),
          form: set(['vehicle'], [vehicle], {}),
        }),
        value,
      ),
      acc,
    );
  }

  return acc;
};

const getGeoLocation = (path) => (acc, source) => {
  const geoLocation = get([GEOLOCATION_NAME], source);
  const distance = getOr(
    DEFAULT_GEOLOCATION_DISTANCE,
    [DISTANCE_NAME],
    geoLocation,
  );

  if (!isEmpty(geoLocation) && geoLocation.lat && geoLocation.lng) {
    return set(
      path,
      {
        value: flow(
          compact,
          join(', '),
        )([
          get(['city'], geoLocation),
          get([distance], getLabelsByValue(distanceOptions)),
        ]),
        form: set([GEOLOCATION_NAME], geoLocation, {}),
      },
      acc,
    );
  }

  return acc;
};

const getSellerType = (path) => (acc, source) => {
  const value = get(['sellerType'], source);
  const seller = find(({ value: sellerValue }) => isEqual(sellerValue, value))(
    sellerTypeOptions,
  );
  const providerValue = get(['provider'], seller);

  if (value && providerValue) {
    return set(
      path,
      {
        value: get(['label'], seller),
        form: set('sellerType', value, {}),
      },
      acc,
    );
  }

  return acc;
};

const getDoors = (path) => (acc, source) => {
  const value = get(['doorRange'], source);
  const doorMin = get(['min'], value);
  const doorMax = get(['max'], value);

  if (!doorMin || !doorMax) {
    return acc;
  }

  return set(
    path,
    {
      value: `${doorMin} / ${doorMax}`,
      form: set('doorRange', value, {}),
    },
    acc,
  );
};

const getConsumption = (path) => (acc, source) => {
  const value = get(['consumptionRange'], source);
  const min = get(['min'], value);
  const max = get(['max'], value);

  if (!min && !max) {
    return acc;
  }

  const label = flow(
    find(({ value: conValue }) => isEqual(value, conValue)),
    get(['label']),
  )(consumptionOptions);

  return set(
    path,
    {
      value: label,
      form: set('consumptionRange', value, {}),
    },
    acc,
  );
};

const mapPathWithOptions = (path, options) => (acc, source) => {
  const value = get([path], source);
  const label = flow(find({ value }), get(['label']))(options);

  return set(
    path,
    {
      value: label,
      form: set(path, value, {}),
    },
    acc,
  );
};

const getSeats = (path) => (acc, source) => {
  const value = get(['seatRange'], source);
  const seatMin = get(['min'], value);
  const seatMax = get(['max'], value);

  if (!seatMin && !seatMax) {
    return acc;
  }

  const label = flow(
    find(({ value: seatValue }) => isEqual(value, seatValue)),
    get(['label']),
  )(seatOptions);

  return set(
    path,
    {
      value: label,
      form: set('seatRange', value, {}),
    },
    acc,
  );
};

const formatFromBool = (formFieldPath) =>
  flow(get([formFieldPath]), (value) => ({
    value,
    form: set(formFieldPath, value, {}),
  }));

export default (makeOptions = []) =>
  (source) =>
    getMapper(
      [
        mapCheckboxList(['airbag'], airbagOptions),
        mapCheckboxList(
          ['bodyType'],
          concat(carBodyTypeOptions, commercialCarBodyTypeOptions),
        ),
        mapCheckboxList(['color'], colorOptions),
        mapPathWithOptions('emissionSticker', emissionStickerOptions),
        mapPathWithOptions('envStandard', envStandardOptions),
        mapCheckboxList(['extras'], extraOptions),
        movePath(['doors'], getDoors),
        movePath(['consumption'], getConsumption),
        mapCheckboxList(['fuelType'], fuelTypeOptions),
        movePath(['geoLocation'], getGeoLocation),
        mapCheckboxList(['interior'], interiorOptions),
        movePath(['metallic'], getMetallic),
        mapPathAndFormat(
          'mileageRangeMin',
          ['mileageRange', 'min'],
          formatDistance,
        ),
        mapPathAndFormat(
          'mileageRangeMax',
          ['mileageRange', 'max'],
          formatDistance,
        ),
        mapCheckboxList(['parkingSensors'], parkingSensorOptions),
        mapPathAndFormat(
          'powerRangeMin',
          ['powerRange', 'min'],
          powerFormatter,
        ),
        mapPathAndFormat(
          'powerRangeMax',
          ['powerRange', 'max'],
          powerFormatter,
        ),
        mapPathWithOptions('prevOwner', prevOwnerOptions),
        mapPathAndFormat('priceRangeMin', ['priceRange', 'min'], formatMoney),
        mapPathAndFormat('priceRangeMax', ['priceRange', 'max'], formatMoney),
        mapPathAndFormat(
          'monthlyPriceRangeMin',
          ['monthlyPriceRange', 'min'],
          formatMoney,
        ),
        mapPathAndFormat(
          'monthlyPriceRangeMax',
          ['monthlyPriceRange', 'max'],
          formatMoney,
        ),
        movePath(['seats'], getSeats),
        mapCheckboxList(['additional'], additionalOptions),
        mapCheckboxList(['light'], lightOptions),
        mapCheckboxList(['multimedia'], multimediaOptions),
        mapCheckboxList(['comfort'], comfortOptions),
        mapCheckboxList(['seatComfort'], seatComfortOptions),
        mapCheckboxList(['security'], securityOptions),
        mapCheckboxList(['serviceHistory'], serviceHistoryOptions),
        movePath(['sellerType'], getSellerType),
        mapCheckboxList(['transmission'], transmissionOptions),
        mapCheckboxList(['upholstery'], upholsteryOptions),
        movePath(['vehicle'], getVehicle(makeOptions)),
        mapPath(['notDamaged'], formatFromBool([NOT_DAMAGED_NAME])),
        mapPath(['smoker'], formatFromBool([SMOKER_NAME])),
        mapPathAndFormat('yearRangeMin', ['yearRange', 'min']),
        mapPathAndFormat('yearRangeMax', ['yearRange', 'max']),
        cleanEmptyValues,
      ],
      {},
    )()(source);
